import axios from 'axios';
import { decodificaPayload, isRispostaCodificata } from '../utils/responseDecoder';

// Configurazione di base
const API_URL = '/api';  // Cambiato da http://localhost:3000/api a /api per usare il proxy

// Configurazione per mock/test (simulare API offline)
const USE_MOCK = false; // Imposta su false per utilizzare il database reale

// Istanza axios configurata
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  // Timeout di 5 secondi per le richieste
  timeout: 5000
});

// Funzione per salvare i clienti mock nel localStorage
const saveMockClients = (clients) => {
  try {
    localStorage.setItem('wapoo_mock_clients', JSON.stringify(clients));
// console.log('Mock clients salvati in localStorage:', clients.length);
  } catch (e) {
    console.error('Errore nel salvataggio dei mock clients:', e);
  }
};

// Funzione per caricare i clienti mock dal localStorage
const loadMockClients = () => {
  try {
    const storedClients = localStorage.getItem('wapoo_mock_clients');
    if (storedClients) {
      return JSON.parse(storedClients);
    }
  } catch (e) {
    console.error('Errore nel caricamento dei mock clients:', e);
  }
  return null;
};

// Decoder globale per chiamate fetch() dirette
const _fetchOriginale = window.fetch;
window.fetch = async (...args) => {
  const response = await _fetchOriginale(...args);
  const jsonOriginale = response.json.bind(response);
  response.json = async () => {
    const data = await jsonOriginale();
    if (isRispostaCodificata(data)) {
      try { return decodificaPayload(data._p); } catch (e) { return data; }
    }
    return data;
  };
  return response;
};

// Decoder globale per chiamate axios dirette (non tramite api/networkApi)
axios.interceptors.response.use(
  response => {
    if (isRispostaCodificata(response.data)) {
      try { response.data = decodificaPayload(response.data._p); } catch (e) { /* fallback */ }
    }
    return response;
  },
  error => {
    if (error.response && isRispostaCodificata(error.response.data)) {
      try { error.response.data = decodificaPayload(error.response.data._p); } catch (e) { /* fallback */ }
    }
    return Promise.reject(error);
  }
);

// Interceptor per le richieste - verifica autorizzazioni prima di inviare
api.interceptors.request.use(
  (config) => {
    // Aggiungi token di autenticazione se presente
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // TODO: Aggiungere qui logica per verificare autorizzazioni
    // Prima di fare chiamate API sensibili
// console.log(`📡 API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag per disabilitare logout automatico durante il login
let isLoggingIn = false;

// Funzione per impostare la flag di login
export const setLoggingIn = (value) => {
  isLoggingIn = value;
};

// Interceptor per la risposta
api.interceptors.response.use(
  response => {
    if (isRispostaCodificata(response.data)) {
      try {
        response.data = decodificaPayload(response.data._p);
      } catch (e) { /* fallback: dati originali */ }
    }
    return response;
  },
  error => {
    if (error.response && isRispostaCodificata(error.response.data)) {
      try {
        error.response.data = decodificaPayload(error.response.data._p);
      } catch (e) { /* fallback */ }
    }
    console.error('Errore nella risposta:', error);
    // Gestione errori centralizzata
    if (error.response) {
      // Il server ha risposto con uno status code diverso da 2xx
      if (error.response.status === 401 && !isLoggingIn) {
        // Reindirizza al login se non autorizzato (ma non durante il processo di login)
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      } else if (error.response.status === 403 && error.response.data?.codice === 'NODO_RETE_INATTIVO') {
        // Gestione specifica per nodo rete inattivo
// console.log('🚫 Utente disattivato per nodo rete inattivo');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Mostra un messaggio informativo prima del redirect
        const message = error.response.data?.message || 'Account disattivato per nodo rete inattivo';
        
        // Salva il messaggio per mostrarlo nella pagina di login
        localStorage.setItem('loginMessage', JSON.stringify({
          type: 'nodo_rete_inattivo',
          message: message
        }));
        
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Helper per simulare risposte API
const mockResponse = (data, delay = 500) => {
// console.log('Mock response creata con dati:', data);
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({ data });
    }, delay);
  });
};

// Simulazione di errori
const mockError = (status, message, errors = []) => {
  return new Promise((_, reject) => {
    setTimeout(() => {
      reject({
        response: {
          status,
          data: {
            error: message,
            errors
          }
        }
      });
    }, 500);
  });
};

// Funzioni API
const apiService = {
  // Auth
  login: async (credentials) => {
    try {
      if (USE_MOCK) {
        // Mock di risposta per il login
        if (credentials.username === 'admin' && credentials.password === 'password') {
          return mockResponse({ 
            token: 'mock-token-123456', 
            user: { id: 1, username: 'admin', nome: 'Admin', cognome: 'User', role: 'admin' } 
          });
        } else {
          return mockError(401, 'Credenziali non valide');
        }
      }
      const response = await api.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  logout: async () => {
    try {
      if (USE_MOCK) {
        localStorage.removeItem('token');
        return mockResponse({ success: true });
      }
      const response = await api.post('/auth/logout');
      localStorage.removeItem('token');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Clients
  getClients: async (params = {}) => {
    try {
      if (USE_MOCK) {
// console.log('getClients: usando', mockClients.length, 'clienti mock');
        
        // Simuliamo la paginazione e filtri
        let filteredClients = [...mockClients];
        
        // Filtro per tipo cliente
        if (params.tipo_cliente) {
          filteredClients = filteredClients.filter(c => c.tipo_cliente === params.tipo_cliente);
        }
        
        // Filtro per stato
        if (params.stato) {
          filteredClients = filteredClients.filter(c => c.stato === params.stato);
        }
        
        // Filtro per ricerca
        if (params.search) {
          const search = params.search.toLowerCase();
          filteredClients = filteredClients.filter(c => 
            (c.nome && c.nome.toLowerCase().includes(search)) ||
            (c.cognome && c.cognome.toLowerCase().includes(search)) ||
            (c.ragione_sociale && c.ragione_sociale.toLowerCase().includes(search)) ||
            (c.email && c.email.toLowerCase().includes(search))
          );
        }
        
        // Assicurarsi che ogni cliente abbia una data di creazione
        filteredClients = filteredClients.map(client => ({
          ...client,
          createdAt: client.createdAt || new Date().toISOString(),
          updatedAt: client.updatedAt || new Date().toISOString()
        }));
        
        // Simuliamo l'ordinamento
        const sortField = params.sort_by || 'createdAt';
        const sortDirection = params.sort_order || 'DESC';
        
        filteredClients.sort((a, b) => {
          const aValue = a[sortField] || '';
          const bValue = b[sortField] || '';
          
          if (sortDirection === 'ASC') {
            return aValue > bValue ? 1 : -1;
          } else {
            return aValue < bValue ? 1 : -1;
          }
        });
        
        // Simuliamo la paginazione
        const page = parseInt(params.page) || 1;
        const limit = parseInt(params.limit) || 10;
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedClients = filteredClients.slice(startIndex, endIndex);
        
        // Creiamo l'oggetto risposta
        const responseData = {
          clients: paginatedClients,
          total: filteredClients.length,
          totalPages: Math.ceil(filteredClients.length / limit),
          page: page
        };
        
// console.log('Invio risposta mock per getClients:', responseData);
        return mockResponse(responseData);
      }
      
      const response = await api.get('/clienti', { params });
      return response.data;
    } catch (error) {
      console.error('Errore in getClients:', error);
      throw error;
    }
  },
  
  getClient: async (id) => {
    try {
      if (USE_MOCK) {
        const client = mockClients.find(c => c.id === parseInt(id));
        if (client) {
          return mockResponse(client);
        } else {
          return mockError(404, 'Cliente non trovato');
        }
      }
      
      const response = await api.get(`/clienti/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  createClient: async (clientData) => {
    try {
// console.log('Dati cliente da inviare:', clientData);
      
      // Validazione lato client dei campi obbligatori
      const missingFields = [];
      
      if (clientData.tipo_cliente === 'privato') {
        // Clienti privati (standard) - nome e cognome obbligatori
        if (!clientData.nome || clientData.nome.trim() === '') {
          missingFields.push({
            field: 'nome',
            message: 'Il nome è obbligatorio'
          });
        }
        
        if (!clientData.cognome || clientData.cognome.trim() === '') {
          missingFields.push({
            field: 'cognome',
            message: 'Il cognome è obbligatorio'
          });
        }
      } else if (clientData.tipo_cliente === 'azienda') {
        // Clienti aziendali (garante) - ragione sociale obbligatoria
        if (!clientData.ragione_sociale || clientData.ragione_sociale.trim() === '') {
          missingFields.push({
            field: 'ragione_sociale',
            message: 'La ragione sociale è obbligatoria'
          });
        }
      }
      
      // Campi obbligatori per tutti i tipi di cliente
      if (!clientData.email || clientData.email.trim() === '') {
        missingFields.push({
          field: 'email',
          message: 'L\'email è obbligatoria'
        });
      } else if (!/\S+@\S+\.\S+/.test(clientData.email)) {
        missingFields.push({
          field: 'email',
          message: 'Email non valida'
        });
      }
      
      if (!clientData.telefono || clientData.telefono.trim() === '') {
        missingFields.push({
          field: 'telefono',
          message: 'Il telefono è obbligatorio'
        });
      }
      
      // Se ci sono campi mancanti, generiamo un errore
      if (missingFields.length > 0) {
// console.log('Validazione fallita:', missingFields);
        throw {
          response: {
            status: 400,
            data: {
              errors: missingFields
            }
          }
        };
      }
      
      if (USE_MOCK) {
        // Genera ID univoco
        const maxId = mockClients.length > 0 ? Math.max(...mockClients.map(c => c.id)) : 0;
        const newId = maxId + 1;
        
        // Crea il nuovo cliente
        const newClient = {
          id: newId,
          ...clientData,
          stato: clientData.stato || 'prospect',
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
        
        // Aggiungi il nuovo cliente agli mock clients
        mockClients = [...mockClients, newClient];
        
        // Salva i mock clients aggiornati nel localStorage
        saveMockClients(mockClients);
        
// console.log('Cliente creato con successo (mock):', newClient);
// console.log('Ora ci sono', mockClients.length, 'clienti nell\'array mockClients');
        
        return mockResponse(newClient, 1000);
      }
      
      // Preparazione dei dati da inviare
      const dataToSend = {
        ...clientData,
        // Assicuriamo che tipo_cliente sia uno dei valori accettati
        tipo_cliente: ['privato', 'azienda'].includes(clientData.tipo_cliente) 
          ? clientData.tipo_cliente 
          : 'privato'
      };
      
      try {
        // Tenta di inviare i dati al server
        const response = await api.post('/clienti', dataToSend);
// console.log('Risposta creazione cliente:', response.data);
        return response.data;
      } catch (apiError) {
        console.error('Errore API nella creazione del cliente:', apiError);
        
        if (apiError.response) {
          console.error('Stato della risposta:', apiError.response.status);
          console.error('Headers della risposta:', apiError.response.headers);
          console.error('Dati della risposta:', apiError.response.data);
        }
        
        throw apiError;
      }
    } catch (error) {
      console.error('Errore generale nella creazione del cliente:', error);
      throw error;
    }
  },
  
  updateClient: async (id, clientData) => {
    try {
      if (USE_MOCK) {
        const index = mockClients.findIndex(c => c.id === parseInt(id));
        if (index !== -1) {
          const updatedClient = {
            ...mockClients[index],
            ...clientData,
            updatedAt: new Date().toISOString()
          };
          
          // Aggiorna il client nell'array
          mockClients = [
            ...mockClients.slice(0, index),
            updatedClient,
            ...mockClients.slice(index + 1)
          ];
          
          // Salva in localStorage
          saveMockClients(mockClients);
          
          return mockResponse(updatedClient);
        } else {
          return mockError(404, 'Cliente non trovato');
        }
      }
      
      const response = await api.put(`/clienti/${id}`, clientData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteClient: async (id) => {
    try {
      if (USE_MOCK) {
        const index = mockClients.findIndex(c => c.id === parseInt(id));
        if (index !== -1) {
          // Rimuovi il cliente dall'array
          mockClients = [
            ...mockClients.slice(0, index),
            ...mockClients.slice(index + 1)
          ];
          
          // Salva in localStorage
          saveMockClients(mockClients);
          
          return mockResponse({ message: 'Cliente eliminato con successo' });
        } else {
          return mockError(404, 'Cliente non trovato');
        }
      }
      
      const response = await api.delete(`/clienti/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Quotations
  getQuotations: async (params = {}) => {
    try {
      if (USE_MOCK) {
        return mockResponse({ 
          total: 0,
          page: 1,
          totalPages: 0,
          quotations: [] 
        });
      }
      
      const response = await api.get('/quotations', { params });
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  
  // Funzione di test per verificare l'oggetto apiService
  test: () => {
// console.log('apiService è disponibile');
    return true;
  }
};

// Log per debug durante l'importazione
// console.log('Modulo API caricato e configurato');

// Esportazione - api (istanza axios) come default
export { apiService };
export default api;
